//#region Imports Packages and Libs

import {
  STORE_MUTATION,
  TypeComponentName,
  MESSAGE_COMPONENT_TYPE,
  TypeImagePosition,
  TypeTagPosition,
  TypeObjectFit,
  EVENTS,
} from "../enums";
import { Component, Vue, Prop } from "vue-facing-decorator";
import {
  IComponentStyle,
  IDynamicButtonItem,
  IDynamicButtonMap,
  IStyleComponentItem,
  MessageStructureComponent,
} from "../types";
import { store } from "../store/store";
import ContainerTag from "../components/container-tag.vue";
import Button from "../components/button.vue";
import LayoutCarousel from "../components/layout-carousel.vue";
import { __vueEvent, CAROUSEL_RADIUS } from "../constant";
import { IConfig } from "../interfaces";
import * as DynamicButtonManager from "../helpers/carousel/dynamic_button_manager";

//#endregion

//#region Define the component in class-style

@Component({
  components: {
    ContainerTag,
    Button,
    LayoutCarousel,
  },
})
export default class ModalCarousel extends Vue {
  @Prop({
    type: Boolean,
    default: false,
  })
  isBottomSheet: boolean;

  get carouseDetail() {
    return store.state.carouselModalDetail;
  }

  get carouselList() {
    return store.state.carouselModalList;
  }

  get component() {
    return this.carouseDetail && this.carouseDetail.component ? this.carouseDetail.component : {};
  }

  get button1() {
    return this.component.button1;
  }

  get button2() {
    return this.component.button2;
  }

  get button3() {
    return this.component.button3;
  }

  get headerImage() {
    return this.component.headerImage;
  }

  get html() {
    return this.component.html;
  }

  get line1() {
    return this.component.line1;
  }

  get line2() {
    return this.component.line2;
  }

  get tag() {
    return this.component.tag;
  }

  get listItem() {
    return this.component.listItem;
  }

  get settings() {
    return this.carouseDetail && this.carouseDetail.settings ? this.carouseDetail.settings : {};
  }

  get structure() {
    return this.carouseDetail && this.carouseDetail.type ? this.carouseDetail.type : {};
  }

  get layout() {
    return this.carouseDetail && this.carouseDetail.layout ? this.carouseDetail.layout : {};
  }

  startY = 0; // Initial touch position
  isDragging: boolean = false;
  isAtTop: boolean = false;
  translateY = 0; // Position of the bottom sheet
  hideTagLine = false;

  /**
   * Computes the style for the component items based on the carousel settings.
   * @returns {IStyleComponentItem} The component styles.
   */
  get componentStyle(): IStyleComponentItem {
    return this.settings &&
      this.settings.carouselSettings &&
      this.settings.carouselSettings.containerSettings &&
      this.settings.carouselSettings.containerSettings.componentItems
      ? this.settings.carouselSettings.containerSettings.componentItems
      : {};
  }

  /* Bottom sheet element reference */
  get bottomSheet() {
    return this.$refs.carouselBottomSheet as HTMLDivElement;
  }

  /**
   * Retrieves the style properties for a specific component type.
   * @param {TypeComponentName} styleName - The name of the component to retrieve styles for.
   * @returns {Partial<IComponentStyle>} The style object for the specified component.
   */
  getElementStyle(element: TypeComponentName) {
    let style: IComponentStyle = {};
    const elementStyle = this.componentStyle[element];
    if (elementStyle && typeof elementStyle === "object" && !Array.isArray(elementStyle)) style = elementStyle;

    const { textAlign, fontSize, fontWeight, color, backgroundColor, margin, padding, background } = style;

    return {
      textAlign,
      fontSize,
      fontWeight,
      color,
      background: background || backgroundColor,
      margin,
      padding,
    };
  }

  /**
   * Computes and returns the style object for the container element.
   *
   * - Extracts styles from the `carouselItem` key in `componentStyle`.
   * - Ensures `backgroundColor` defaults to `"#FFF"` if not provided.
   * - Ensures `borderRadius` defaults to `CAROUSEL_RADIUS` if not provided.
   *
   * @returns {IComponentStyle} An object containing the computed style properties, including `backgroundColor` and `borderRadius`.
   */
  get styleContainer() {
    let style: IComponentStyle = { backgroundColor: "#FFF", borderRadius: CAROUSEL_RADIUS };
    const elementStyle = this.componentStyle["carouselItem"];
    if (elementStyle && typeof elementStyle === "object" && !Array.isArray(elementStyle)) {
      const { backgroundColor = "#FFF", background, borderRadius = CAROUSEL_RADIUS } = elementStyle;

      style = {
        background: background || backgroundColor,
        borderRadius,
      };
    }

    return style;
  }

  /**
   * Retrieves the full style object for a specific component.
   * @param {TypeComponentName} element - The component name.
   * @returns {IComponentStyle} The style object for the specified component.
   */
  getComponentStyle(element: TypeComponentName) {
    let style: IComponentStyle = {};
    const elementStyle = this.componentStyle[element];
    if (elementStyle && typeof elementStyle === "object" && !Array.isArray(elementStyle)) style = elementStyle;
    return style;
  }

  /**
   * Modifies the carousel's style dynamically based on its layout and structure.
   */
  modifyCarouselStyle() {
    try {
      const carouselPopup = this.$refs.carouselPopup as HTMLDivElement;
      const carouselModalOverlay = this.$refs.carouselModalOverlay as HTMLDivElement;
      const componentWrapper = this.$refs.modalComponentWrapper as HTMLDivElement;
      const buttonWrapper = this.$refs.modalButtonWrapper as HTMLDivElement;
      const wrapperImage = this.$refs.modalWrapperImage as HTMLDivElement;
      const imageEvent = this.$refs.modalImageEvent as HTMLImageElement;
      const modalCarouselClose = this.$refs.modalCarouselClose as HTMLButtonElement;
      const contentTagWrapper = this.$refs.contentTagWrapper as HTMLDivElement;
      const wrapperImageMargin = "12px";

      switch (this.imgPosition) {
        case "top-middle":
          if (this.layout === "list" || this.structure === "card") {
            if (imageEvent) {
              imageEvent.style.width = "100% !important";
            }
          }
          break;

        case "left-top":
          if (wrapperImage) {
            wrapperImage.style.float = "left";
            wrapperImage.style.marginRight = wrapperImageMargin;
          }
          break;

        case "left-middle":
          if (wrapperImage) {
            wrapperImage.style.float = "left";
            wrapperImage.style.marginRight = wrapperImageMargin;
          }
          break;

        case "right-middle":
          if (wrapperImage) {
            wrapperImage.style.float = "right";
            wrapperImage.style.marginLeft = wrapperImageMargin;
          }
          break;

        case "right-top":
          if (wrapperImage) {
            wrapperImage.style.float = "right";
            wrapperImage.style.marginLeft = wrapperImageMargin;
          }
          break;
      }

      if (this.carouseDetail) {
        switch (this.carouseDetail.type) {
          case "bubble":
            /* TODO: Write custom style for carousel bubble */
            break;

          case "card":
            if (componentWrapper) {
              componentWrapper.style.gap = "0";
            }

            if (buttonWrapper) {
              buttonWrapper.style.padding = "12px";
            }
            break;

          case "modal":
            if (componentWrapper) {
              componentWrapper.style.padding = "12px";
            }
            break;
        }
      }

      /**
       * JIRA: SD-7229
       * Adjusts the positioning and height of the carousel popup dynamically based on
       * the overlay, component wrapper, and modal close button dimensions.
       *
       * - Determines the bottom offset based on whether the tagline is hidden.
       * - Sets the `bottom` position of `carouselPopup` to `chatFooterHeight`, with an additional 45px if the tagline is visible.
       * - Sets the `top` position of `carouselPopup` to `20px`.
       * - Calculates the modal content height by summing `componentWrapper.offsetHeight`,
       *   `modalCarouselClose.offsetHeight`, and an extra 16 pixels for spacing.
       * - If 90% of `carouselPopup.offsetHeight` is greater than `modalContentHeight`,
       *   it adjusts the height of `carouselPopup` to match `modalContentHeight`.
       * - Centers the popup vertically within `carouselModalOverlay` using the formula:
       *   `translateY((overlayHeight * 0.5) - (modalContentHeight * 0.5))`.
       */
      if (carouselModalOverlay && carouselPopup && componentWrapper && modalCarouselClose) {
        const bottomOffset = this.hideTagLine ? "var(--chatFooterHeight)" : "calc(var(--chatFooterHeight) + 45px)";
        carouselPopup.style.bottom = bottomOffset;
        carouselPopup.style.top = `20px`;
        const modalContentHeight = componentWrapper.offsetHeight + modalCarouselClose.offsetHeight + 32;

        /**
         * JIRA: SD-7275
         * Adjusts the height and position of the carousel popup if it exceeds the modal content height.
         *
         * If `carouselPopup.offsetHeight` is greater than `modalContentHeight`, it:
         * - Sets the height of `carouselPopup` to `modalContentHeight`.
         * - Calculates `contentOffset` based on the difference between `carouselModalOverlay.offsetHeight`
         *   and `modalContentHeight`, ensuring vertical centering.
         * - Applies `contentOffset` to both `top` and `bottom` styles of `carouselPopup`.
         */
        if (carouselPopup.offsetHeight > modalContentHeight) {
          carouselPopup.style.height = `${modalContentHeight}px`;
          const contentOffset = `${(carouselModalOverlay.offsetHeight - modalContentHeight) * 0.5}px`;
          carouselPopup.style.bottom = contentOffset;
          carouselPopup.style.top = contentOffset;
        }
      }

      /**
       * JIRA: SD-7490
       * Adjusts the style of the `contentTagWrapper` element based on tag orientation and position.
       *
       * This logic is applied only when:
       * - `contentTagWrapper` exists
       * - The tag is not aligned to the top-middle
       * - The `tagOrientation` is set to "content"
       *
       * Behavior:
       * - Sets display to flex for alignment
       * - Conditionally sets the width based on the tag position:
       *    - If position is "top-left" or "top-right", calculates remaining width excluding image width and margin
       *    - Otherwise, sets width to 100%
       * - Aligns content to either `flex-start` or `flex-end` based on tag position
       *
       */
      if (contentTagWrapper && !this.isTopMiddle && this.styleTag.tagOrientation === "content") {
        const tagPosition = this.styleTag.position;
        contentTagWrapper.style.display = "flex";
        contentTagWrapper.style.width =
          tagPosition === "top-left" || tagPosition === "top-right"
            ? `calc(100% - calc(${this.imgWidth} + ${wrapperImageMargin}))`
            : "100%";
        contentTagWrapper.style.justifyContent =
          tagPosition === "top-right" || tagPosition === "bottom-right" ? "flex-end" : "flex-start";
      }
    } catch (error) {
      console.error("Error occurred in modal-carousel.ts -> modifyCarouselStyle", error);
    }
  }

  /**
   * Lifecycle hook: Runs after the component has been mounted to the DOM.
   */
  mounted() {
    this.listenEvents();
    if (this.carouseDetail) this.modifyCarouselStyle();

    /**
     * JIRA: SD-7007
     * To prevent the browser's pull to refresh behavior while swipe down bottom sheet
     *
     */
    if (this.isBottomSheet) {
      document.documentElement.style.overscrollBehaviorY = "contain";
      document.body.style.overscrollBehaviorY = "contain";
    }
  }

  /**
   * Sets up event listeners for the modal carousel component.
   * Listens for configuration events and updates component properties accordingly.
   */
  listenEvents() {
    __vueEvent.once(EVENTS.SetConfig, (config: IConfig) => {
      this.hideTagLine = config.hideTagLine;
    });
  }

  beforeDestroy() {
    /**
     * JIRA: SD-7007
     * To revert the pull to refresh behavior while the bottom sheet is destroyed
     *
     */
    if (this.isBottomSheet) {
      document.documentElement.style.overscrollBehavior = "auto"; // Reset on unmount
      document.body.style.overscrollBehavior = "auto";
    }
  }

  /** Style properties */
  get styleHeaderImage(): IComponentStyle {
    return this.getComponentStyle("headerImage");
  }

  get styleLine1() {
    return this.getElementStyle("line1");
  }

  get styleLine2() {
    return this.getElementStyle("line2");
  }

  get styleHtml() {
    return this.getElementStyle("html");
  }

  get styleButton1() {
    return this.getElementStyle("button1");
  }

  get styleButton2() {
    return this.getElementStyle("button2");
  }

  get styleButton3() {
    return this.getElementStyle("button3");
  }

  /**
   * JIRA: SD-7490
   * Computes and returns the final style object for the tag element.
   *
   * The method merges styles from `getComponentStyle("tag")` with fallback defaults,
   * especially for `borderRadius`, based on the component structure, image position,
   * and image availability.
   *
   * @returns {IComponentStyle} A complete style object for the tag element.
   *
   * ## Logic Summary:
   * - Uses `this.imgBorderRadius` by default for `borderRadius`
   * - If structure is `"card"` with `"top-middle"` image position or no image URL,
   *   it uses `this.styleContainer.borderRadius` or a fallback (`CAROUSEL_RADIUS`)
   * - Adjusts default `borderRadius` based on the tag's `position`:
   *   - `"top-left"` or `"bottom-right"` → `${borderRadius} 0`
   *   - Otherwise → `0 ${borderRadius}`
   * - Merges `tagComponentStyle` with computed `borderRadius` (unless already set)
   */
  get styleTag(): IComponentStyle {
    let borderRadius = this.imgBorderRadius;
    const tagComponentStyle = this.getComponentStyle("tag");

    if ((this.structure === "card" && this.imgPosition === "top-middle") || !this.imageUrl) {
      borderRadius = this.styleContainer.borderRadius || CAROUSEL_RADIUS;
    }

    let defaultBorderRadius = `0 ${borderRadius}`;

    if (tagComponentStyle.position === "top-left" || tagComponentStyle.position === "bottom-right") {
      defaultBorderRadius = `${borderRadius} 0`;
    }

    return {
      ...tagComponentStyle,
      borderRadius: tagComponentStyle.borderRadius || defaultBorderRadius,
    };
  }

  /**
   * Retrieves the tag's content as a string.
   * If the tag or its content is not defined, returns an empty string.
   * @returns {string} The tag content or an empty string if not available.
   */
  get tagContent(): string {
    return this.tag ? this.tag.content : "";
  }

  /**
   * Returns the position of the tag as defined in the `styleTag` configuration.
   * If `styleTag` is not defined, defaults to `"top-left"`.
   * @returns {TypeTagPosition} The tag position, such as `"top-left"`, `"top-right"`, etc.
   */
  get tagPosition(): TypeTagPosition {
    return this.styleTag ? (this.styleTag.position as TypeTagPosition) : "top-left";
  }

  /**
   * Determines which tag type (card, image, button, or content) should be visible,
   * and whether it appears at the top or bottom.
   *
   * A tag is shown if `this.tag` and `this.tag.content` exist.
   * Visibility is based on `tagOrientation` and `tagPosition`.
   * Falls back to an image tag if `imageUrl` exists, otherwise a card tag.
   */
  get toggleTag() {
    const showTag: boolean = !!(this.tag && this.tag.content);

    const tagVisibility = {
      isCardTag: false,
      isImageTag: false,
      isTopButtonTag: false,
      isBottomButtonTag: false,
      isTopContentTag: false,
      isBottomContentTag: false,
    };

    if (showTag) {
      const isTop = this.tagPosition === "top-left" || this.tagPosition === "top-right";
      switch (this.styleTag.tagOrientation) {
        case "button":
          tagVisibility.isTopButtonTag = isTop;
          tagVisibility.isBottomButtonTag = !isTop;
          break;

        case "content":
          tagVisibility.isTopContentTag = isTop;
          tagVisibility.isBottomContentTag = !isTop;
          break;

        case "card":
          tagVisibility.isCardTag = true;
          break;

        default:
          if (this.imageUrl) {
            tagVisibility.isImageTag = true;
          } else {
            tagVisibility.isCardTag = true;
          }
          break;
      }
    }

    return tagVisibility;
  }

  /**
   * Checks if the header image's position is set to "top-middle".
   * @returns {boolean} `true` if the header image is positioned at "top-middle", otherwise `false`.
   */
  get isTopMiddle(): boolean {
    return this.imgPosition === "top-middle";
  }

  /**
   * Determines whether at least one button is present in the carousel.
   * @returns {boolean} `true` if any button is available, otherwise `false`.
   */
  get showButton(): boolean {
    return !!this.button1 || !!this.button2 || !!this.button3;
  }

  /**
   * Retrieves the URL of the header image. Falls back to a placeholder if the URL is not defined.
   * @returns {string} The image URL or "undefined" if no URL is set.
   */
  get imageUrl(): string {
    // let url = `${store.state.AWSServerURL}${store.state.AWSImageLogoPath}Carousel/Placeholder.png`;
    let url = ``;
    if (this.headerImage && this.headerImage.imageUrl) url = this.headerImage.imageUrl;
    else if (this.styleHeaderImage && this.styleHeaderImage.imageUrlPlaceholder)
      url = this.styleHeaderImage.imageUrlPlaceholder;
    return url;
  }

  /**
   * Retrieves the alt text for the header image. Falls back to a placeholder if not defined.
   * @returns {string} The alt text of the header image or an empty string if not set.
   */
  get imageAlt(): string {
    let alt = "IMAGE";
    if (this.headerImage && this.headerImage.alt) alt = this.headerImage.alt;
    else if (this.styleHeaderImage && this.styleHeaderImage.imageAltPlaceholder)
      alt = this.styleHeaderImage.imageAltPlaceholder;
    return alt;
  }

  /**
   * JIRA: SD-7369
   * Gets the aspect-ratio style for the header image.
   *
   * @returns {string | undefined} The aspect-ratio value, defaulting to null if not specified.
   */
  get imgAspectRatio(): string | undefined {
    let aspectRatio: string | undefined = undefined;
    if (this.styleHeaderImage && this.styleHeaderImage.aspectRatio) {
      aspectRatio = this.styleHeaderImage.aspectRatio;
    }
    return aspectRatio;
  }

  /**
   * Retrieves the width of the header image from its style.
   * @returns {string | undefined} The width of the header image or an empty string if not set.
   */
  get imgWidth(): string | undefined {
    let width: string | undefined = this.isTopMiddle ? "100%" : "100px";
    if (this.styleHeaderImage && this.styleHeaderImage.width) {
      /**
       * JIRA: SD-7369
       * Remove the width if image has width and aspectRatio
       */
      if (this.imgAspectRatio && this.styleHeaderImage.height) {
        width = undefined;
      } else if (this.styleHeaderImage.width) {
        width = this.styleHeaderImage.width;
      }
    }
    return width;
  }

  /**
   * Retrieves the height of the header image from its style.
   * @returns {string | null} The height of the header image or an empty string if not set.
   */
  get imgHeight(): string | undefined {
    let height: string | undefined = this.isTopMiddle ? "auto" : "100px";
    if (this.styleHeaderImage) {
      /**
       * JIRA: SD-7369
       * Remove the height if image has width and aspectRatio
       */
      if (this.imgAspectRatio && this.styleHeaderImage.width) {
        height = undefined;
      } else if (this.styleHeaderImage.height) {
        height = this.styleHeaderImage.height;
      }
    }
    return height;
  }

  /**
   * Retrieves the position of the header image from its style. Defaults to "top-middle".
   * @returns {TypeImagePosition} The position of the header image.
   */
  get imgPosition(): TypeImagePosition {
    let position: TypeImagePosition = "top-middle";
    if (this.styleHeaderImage && this.styleHeaderImage.position) {
      position = this.styleHeaderImage.position as TypeImagePosition;
    }
    return position;
  }

  /**
   * JIRA: SD-7369
   * Gets the object-fit style for the header image.
   *
   * @returns {TypeObjectFit | undefined} The object-fit value, defaulting to "none" if not specified.
   */
  get imgObjectFit(): TypeObjectFit | undefined {
    let objectFit: TypeObjectFit | undefined = undefined;
    if (this.styleHeaderImage && this.styleHeaderImage.objectFit) {
      objectFit = this.styleHeaderImage.objectFit as TypeObjectFit;
    }
    return objectFit;
  }

  /**
   * Computes and returns the border-radius value for the header image.
   *
   * - If `structure` is `"card"`, the border-radius is set to `"0"`.
   * - Otherwise, if `styleHeaderImage` has a `borderRadius` property, it uses that value.
   * - Defaults to `CAROUSEL_RADIUS` if no other conditions are met.
   *
   * @returns {string} The computed border-radius value for the image.
   */
  get imgBorderRadius(): string {
    let borderRadius = CAROUSEL_RADIUS;
    if (this.structure === "card") {
      borderRadius = "0";
    } else if (this.styleHeaderImage && this.styleHeaderImage.borderRadius) {
      borderRadius = this.styleHeaderImage.borderRadius;
    }
    return borderRadius;
  }

  /**
   * Generates properties for a button component based on its name.
   * @param {TypeComponentName} element - The name of the button component (e.g., "button1", "button2").
   * @returns {MessageStructureComponent} The properties for the specified button component.
   */
  getButtonProps(element: TypeComponentName): MessageStructureComponent {
    return {
      ...this.component[element],
      type:
        this.componentStyle[element] && this.componentStyle[element].type
          ? (this.componentStyle[element].type as MESSAGE_COMPONENT_TYPE)
          : MESSAGE_COMPONENT_TYPE.Button,
    };
  }

  /**
   * JIRA: SD-6998
   * Handles button click event.
   * Closes either `carouselDetail` or `carouselList` modal based on availability.
   */
  onClickButton() {
    if (this.carouseDetail) {
      this.closeModalDetail();
    }

    if (this.carouselList) {
      this.closeModalList();
    }
  }

  /**
   * Retrieves the properties for the first button component.
   * @returns {MessageStructureComponent} The properties for "button1".
   */
  get button1Component(): MessageStructureComponent {
    return this.getButtonProps("button1");
  }

  /**
   * Retrieves the properties for the second button component.
   * @returns {MessageStructureComponent} The properties for "button2".
   */
  get button2Component(): MessageStructureComponent {
    return this.getButtonProps("button2");
  }

  /**
   * Retrieves the properties for the third button component.
   * @returns {MessageStructureComponent} The properties for "button3".
   */
  get button3Component(): MessageStructureComponent {
    return this.getButtonProps("button3");
  }

  /**
   * JIRA: SD-7005
   * Manage the Firefox drag issue fix since it's not recognizing the Touchevent.
   */
  getBottomSheetClientY(event: TouchEvent | MouseEvent) {
    return typeof TouchEvent !== "undefined" && event instanceof TouchEvent
      ? event.touches[0].clientY
      : typeof MouseEvent !== "undefined" && event instanceof MouseEvent
        ? event.clientY
        : 0; // Get Y position
  }

  /**
   * Closes the carousel modal detail by committing a null value to the Vuex store.
   */
  closeModalDetail() {
    store.commit(STORE_MUTATION.UpdateCarouselModalDetail, null);
  }

  /**
   * Closes the carousel modal list by committing a null value to the Vuex store.
   */
  closeModalList() {
    store.commit(STORE_MUTATION.UpdateCarouselModalList, null);
  }

  /**
   * JIRA: SD-6998
   * Handles pointer down event (touch or mouse).
   * Captures the initial Y position when the user starts dragging.
   *
   * @param {TouchEvent | MouseEvent} event - The pointer event object.
   */
  onDragStart(event: TouchEvent | MouseEvent) {
    if (!this.isAtTop) return; // Allow normal scrolling if not at the top

    this.onDragStartHandle(event);
  }

  /**
   * JIRA: SD-6998
   * Handles pointer down event (touch or mouse).
   * Captures the initial Y position when the user starts dragging.
   * Irrespective of the scroll list position we can swipe down the bottom sheet using the handlebar
   *
   * @param {TouchEvent | MouseEvent} event - The pointer event object.
   */
  onDragStartHandle(event: TouchEvent | MouseEvent) {
    try {
      // event.preventDefault(); // Prevents unwanted page scrolling
      this.isDragging = true;
      this.startY = this.getBottomSheetClientY(event);

      window.addEventListener("mouseup", this.onDragEnd);
      window.addEventListener("touchend", this.onDragEnd);
    } catch (error) {
      console.error("Error occurred in 'modal-carousel.ts > onDragStartHandle': ", error);
    }
  }

  /**
   * JIRA: SD-6998
   * Handles pointer move event (touch or mouse).
   * Updates the modal's position dynamically as the user drags.
   *
   * @param {TouchEvent | MouseEvent} event - The pointer event object.
   */
  onDragMove(event: TouchEvent | MouseEvent) {
    try {
      if (!this.isDragging) return;

      const currentY = this.getBottomSheetClientY(event);
      const deltaY = currentY - this.startY;

      if (deltaY > 0) {
        this.translateY = deltaY;
      }
    } catch (error) {
      console.error("Error occurred in 'modal-carousel.ts > onDragMove': ", error);
    }
  }

  /**
   * JIRA: SD-6998
   * Handles pointer up event (touch or mouse).
   * Determines whether to close the modal based on drag distance.
   */
  onDragEnd() {
    try {
      if (!this.isDragging) return;

      if (this.translateY > 200) {
        this.closeModalList();
      } else {
        this.translateY = 0;
      }

      this.isDragging = false;

      window.removeEventListener("mouseup", this.onDragEnd);
      window.removeEventListener("touchend", this.onDragEnd);
    } catch (error) {}
  }

  /**
   * JIRA: SD-7007
   * Handle scroll behavior to detect when at top
   */
  onScroll(event: UIEvent) {
    try {
      const scrollContainer = event.target as HTMLDivElement;

      if (scrollContainer) {
        this.isAtTop = scrollContainer.scrollTop === 0;
      }
    } catch (error) {}
  }

  //#region Support Dynamic buttons

  /**
   * Gets all dynamic buttons from the component's buttons object.
   * Filters and transforms valid button data into renderable button items.
   *
   * @returns Array of dynamic button items with key, component, and style
   */
  get allDynamicButtons(): IDynamicButtonItem[] {
    try {
      const buttonsObject = this.getValidButtonsObject();
      if (!buttonsObject) return [];

      // Transform buttons with access to component style
      return DynamicButtonManager.transformButtonsToItems(buttonsObject, this.componentStyle);
    } catch (ex) {
      console.error("[modal-carousel.ts] An error has occurred \n-> Inside allDynamicButtons -> Error: ", ex);
      return [];
    }
  }

  /**
   * Validates and returns the buttons object from component if it exists and is valid
   *
   * @returns The buttons object or null if invalid
   */
  getValidButtonsObject(): IDynamicButtonMap | null {
    const buttonsObj = this.component?.buttons;

    if (!DynamicButtonManager.isValidButtonsObject(buttonsObj)) {
      return null;
    }

    return buttonsObj;
  }

  //#endregion
}

//#endregion
